import moment from 'moment';
import { RRule } from 'rrule';

import { EndOption, Freq } from './constants';

export const isDate = (date: any) => !isNaN(new Date(date).getTime());

export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD HH:mm';

export const formatDate = (date: Date, format: string = DEFAULT_DATE_FORMAT, utc: boolean = false) => {
  if (utc) {
    return moment(date).utc().format(format);
  }
  return moment(date).format(format);
}

export const formatInputDate = (date: Date) => formatDate(date, DEFAULT_DATE_FORMAT, false);

export const parseDate = (date: string, format: string = DEFAULT_DATE_FORMAT): Date => new Date(date);

export const dateRoundUpHour = (date: Date | null = null) => {
  if (date === null) {
    date = new Date(); // now
  }
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours() + 1,
    0,
    0,
  );
}

const RRULE_DATE_FORMAT = 'YYYYMMDDTHHmm00';

const buildStartDate = (startDate: Date, tz?: string) => {
  let utc = 'Z';
  let tzId = '';

  if (tz) {
    utc = ''; // unset utc if timezone is set
    tzId = `;TZID=${tz}`;
  }

  return `DTSTART${tzId}:${formatDate(startDate, RRULE_DATE_FORMAT, false)}${utc}`; // date is in UTC
};

const buildEnd = (option: number | string, after: number | string, endDate: Date): string => {
  switch (option) {
    case EndOption.AFTER:
      return `COUNT=${after}`;
    case EndOption.ON_DATE:
      const utc = 'Z';
      return `UNTIL=${formatDate(endDate, RRULE_DATE_FORMAT)}${utc}`;
  }
  return '';
}

export const buildRRule = (data: any) => {
  const {
    freq,
    interval,

    byHour,

    weekdays,

    monthlyOn,
    monthDay,
    monthRelativeDay,
    monthWeekDay,

    yearlyOn,
    yearMonth,
    yearMonthDay,
    yearMonthRelativeDay,
    yearMonthWeekDay,
    tzId,
  } = data;

  const startDate = buildStartDate(data.startDate, tzId);

  const items: string[] = [];
  items.push(`FREQ=${freq}`);
  switch (freq) {
    case Freq.YEARLY:
      if (parseInt(yearlyOn) === 1 && yearMonth && yearMonthDay) {
        items.push(`BYMONTH=${yearMonth}`);
        items.push(`BYMONTHDAY=${yearMonthDay}`);
      } else if (parseInt(yearlyOn) === 2 && yearMonthRelativeDay && yearMonthWeekDay && yearMonth) {
        items.push(`BYSETPOS=${yearMonthRelativeDay}`);
        items.push(`BYDAY=${yearMonthWeekDay}`);
        items.push(`BYMONTH=${yearMonth}`);
      }
      break;
    case Freq.MONTHLY:
      items.push(`INTERVAL=${interval}`);

      if (parseInt(monthlyOn) === 1 && monthDay) { // on n-th day of the month
        items.push(`BYMONTHDAY=${monthDay}`); // -1 means last day
      } else if (parseInt(monthlyOn) === 2 && monthRelativeDay && monthWeekDay) { // on relative month day / week day
        items.push(`BYSETPOS=${monthRelativeDay}`);
        items.push(`BYDAY=${monthWeekDay}`);
      }
      break;
    case Freq.WEEKLY:
      items.push(`INTERVAL=${interval}`);

      if (weekdays.length) {
        items.push(`BYDAY=${weekdays.join(',')}`);
      }
      break;
    case Freq.DAILY:
      items.push(`INTERVAL=${interval}`);
      break;
    case Freq.HOURLY:
    case Freq.MINUTELY:
      items.push(`INTERVAL=${interval}`);

      if (byHour?.length > 0) {
        items.push(`BYHOUR=${byHour.join(',')}`);
      }
      if (weekdays?.length > 0 && weekdays?.length < 7) {
        /*
         * At least one day set, but not all.
         *
         * If all days are set (length === 7), then we don't have to specify `by week day`, as default is
         *   `all`.
         */
        items.push(`BYDAY=${weekdays.join(',')}`);
      }
      break;
  }
  const end = buildEnd(data.endOption, data.endAfter, data.endOnDate);
  if (end) {
    items.push(end);
  }

  const rrule = items.join(';');

  return `${startDate};\nRRULE:${rrule}`;
};

export const rruleNextRun = (rrule: string): string | null => {
  if (!rrule) {
    return null;
  }

  try {
    const rruleObj = RRule.fromString(rrule);

    return formatDate(rruleObj.after(new Date()), DEFAULT_DATE_FORMAT);
  } catch (e) {
    return null;
  }
};

export const rruleToText = (rrule: string): string | null => {
  if (!rrule) {
    return null;
  }

  try {
    const rruleObj = RRule.fromString(rrule);

    return (rruleObj.toText() || '').replace('(~ approximate)', '');
  } catch (e) {
    return null;
  }
};

export const fromStrToRRule = (rrule: string): RRule | null => {
  try {
    return RRule.fromString(rrule);
  } catch (e) {
    return null;
  }
};

export const intervalValue = (value: number): number => {
  /*
   * interval cannot be lower than 1
   */
  return parseInt('' + value) <= 1 ? 1 : value;
};

export const executeOnly = (sendObj: any | undefined | null) => {
  /*
   * Schedule action is execute only
   */
  if (!sendObj) {
    return false;
  }
  return sendObj?.action === 'execute';
}

export const subtractTimezoneOffset = (date: Date): Date => {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
}

export const addTimezoneOffset = (date: Date): Date => {
  return new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
}

export const timezoneOffset = (date: Date): Date => {
  /*
   * Adjust date to timezone offset
   */
  if (date.getTimezoneOffset() >= 0) {
    return addTimezoneOffset(date);
  } else {
    return subtractTimezoneOffset(date);
  }
}

export const convertStartDate = (date: Date): Date => {
  const addZero = (value: number) => value < 10 ? ('0' + value) : value;

  const format = `${date.getFullYear()}-` +
    `${addZero(date.getUTCMonth() + 1)}-` +
    `${addZero(date.getUTCDate())}T` +
    `${addZero(date.getUTCHours())}:` +
    `${addZero(date.getUTCMinutes())}:` +
    `${addZero(date.getUTCSeconds())}`;

  return new Date(format);
}

export const getByHourInterval = (byHour?: number[]) => {
  if (!byHour) {
    return {
      from: '0',
      until: '23',
    }
  }
  const _byHour = byHour.map((v: number) => parseInt('' + v));

  return {
    from: Math.min(..._byHour),
    until: Math.max(..._byHour),
  }
}

export const buildByHourInterval = (_from: number | string, until: number | string) => {
  _from = parseInt('' + _from);
  until = parseInt('' + until);

  const byHour = [];
  for (let index = _from; index <= until; index++) {
    byHour.push(index);
  }
  return byHour;
}
